import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Thermometer, Sparkles, Map, Plus, FileDown, Save, AlertTriangle } from "lucide-react";

export default function WarehouseOps() {
  return (
    <div className="space-y-6" data-testid="warehouse-ops-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Bodega · Operaciones BPA</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Cadena de frío y limpieza</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Registro por franjas horarias 8:00-16:30 según buenas prácticas farmacéuticas.</p>
      </div>
      <Tabs defaultValue="temp">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="temp"><Thermometer size={14} className="mr-2" />Temperatura</TabsTrigger>
          <TabsTrigger value="limp"><Sparkles size={14} className="mr-2" />Limpieza</TabsTrigger>
          <TabsTrigger value="ubi"><Map size={14} className="mr-2" />Ubicaciones</TabsTrigger>
        </TabsList>
        <TabsContent value="temp"><TemperaturaFranjas /></TabsContent>
        <TabsContent value="limp"><LimpiezaTurnos /></TabsContent>
        <TabsContent value="ubi"><Ubicaciones /></TabsContent>
      </Tabs>
    </div>
  );
}

function useBodegas() {
  const [b, setB] = useState([]);
  useEffect(() => { api.get("/masters/catalog/bodegas").then(r => setB(r.data)).catch(() => {}); }, []);
  return b;
}

function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthAgoISO() { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); }

function downloadPDF(path) {
  const url = `${BACKEND_URL}/api${path}`;
  fetch(url, { credentials: "include" })
    .then(r => { if (!r.ok) throw new Error("PDF error"); return r.blob(); })
    .then(blob => {
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = path.split("/").pop().split("?")[0] + ".pdf";
      a.click();
      URL.revokeObjectURL(u);
    })
    .catch((err) => { console.error("downloadPDF failed:", err); toast.error("Error al generar PDF"); });
}

function TemperaturaFranjas() {
  const bodegas = useBodegas();
  const [bodegaId, setBodegaId] = useState("");
  const [config, setConfig] = useState({ franjas: [], rangos: {} });
  const [fechaForm, setFechaForm] = useState(todayISO());
  const [filas, setFilas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [pdfDesde, setPdfDesde] = useState(monthAgoISO());
  const [pdfHasta, setPdfHasta] = useState(todayISO());

  useEffect(() => {
    api.get("/warehouse-ops/temperatura-franjas/config").then(r => setConfig(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (bodegas.length && !bodegaId) setBodegaId(bodegas[0].id);
  }, [bodegas, bodegaId]);

  const loadDia = useCallback(async () => {
    if (!bodegaId || !fechaForm || !config.franjas?.length) return;
    try {
      const { data } = await api.get(`/warehouse-ops/temperatura-franjas?bodega_id=${bodegaId}&desde=${fechaForm}&hasta=${fechaForm}`);
      const map = Object.fromEntries(data.map(r => [r.hora_inicio, r]));
      setFilas(config.franjas.map(f => {
        const e = map[f.hora_inicio] || {};
        return {
          hora_inicio: f.hora_inicio, hora_fin: f.hora_fin,
          temp_general: e.temp_general ?? "", hum_general: e.hum_general ?? "",
          temp_refrigerador: e.temp_refrigerador ?? "", hum_refrigerador: e.hum_refrigerador ?? "",
          temp_congelador: e.temp_congelador ?? "", hum_congelador: e.hum_congelador ?? "",
          observacion: e.observacion ?? "",
          guardado: !!e.id,
        };
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [bodegaId, fechaForm, config.franjas]);

  const loadHistorico = useCallback(async () => {
    if (!bodegaId) return;
    try {
      const { data } = await api.get(`/warehouse-ops/temperatura-franjas?bodega_id=${bodegaId}&desde=${monthAgoISO()}&hasta=${todayISO()}`);
      setHistorico(data);
    } catch (err) { console.warn("loadHistorico temperatura:", err); }
  }, [bodegaId]);

  useEffect(() => { loadDia(); loadHistorico(); }, [loadDia, loadHistorico]);

  const updateFila = (idx, field, value) => {
    setFilas(f => { const c = [...f]; c[idx] = { ...c[idx], [field]: value }; return c; });
  };

  const guardarFila = async (idx) => {
    const f = filas[idx];
    if (!bodegaId) return toast.error("Seleccione una bodega");
    const num = (v) => (v === "" || v == null) ? null : parseFloat(v);
    try {
      await api.post("/warehouse-ops/temperatura-franjas", {
        bodega_id: bodegaId, fecha: fechaForm,
        hora_inicio: f.hora_inicio, hora_fin: f.hora_fin,
        temp_general: num(f.temp_general), hum_general: num(f.hum_general),
        temp_refrigerador: num(f.temp_refrigerador), hum_refrigerador: num(f.hum_refrigerador),
        temp_congelador: num(f.temp_congelador), hum_congelador: num(f.hum_congelador),
        observacion: f.observacion,
      });
      toast.success(`Franja ${f.hora_inicio} guardada`);
      loadDia(); loadHistorico();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const guardarTodas = async () => {
    let count = 0;
    for (let i = 0; i < filas.length; i++) {
      const f = filas[i];
      const tieneDatos = ["temp_general", "temp_refrigerador", "temp_congelador"].some(k => f[k] !== "" && f[k] != null);
      if (tieneDatos) { await guardarFila(i); count++; }
    }
    if (count === 0) toast.info("No hay franjas con datos para guardar");
  };

  const fueraRango = (val, zona) => {
    if (val === "" || val == null) return false;
    const r = config.rangos?.[zona];
    if (!r) return false;
    const n = parseFloat(val);
    return n < r.min || n > r.max;
  };

  const fueraRangoCount = historico.filter(h => Object.values(h.fuera_rango || {}).some(v => v === true)).length;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white border border-[#d1dbd6] rounded-md p-3">
        <div className="flex-1 min-w-[180px]">
          <Label>Bodega</Label>
          <Select value={bodegaId} onValueChange={setBodegaId}>
            <SelectTrigger data-testid="temp-bodega"><SelectValue placeholder="..." /></SelectTrigger>
            <SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Fecha del registro</Label>
          <Input type="date" value={fechaForm} onChange={(e) => setFechaForm(e.target.value)} data-testid="temp-fecha" />
        </div>
        <Button onClick={guardarTodas} className="bg-[#205c45]" data-testid="temp-guardar-todas"><Save size={14} className="mr-1" />Guardar todas las franjas</Button>
      </div>

      <div className="text-xs text-[#5c7368] italic">
        Rangos válidos: General <strong>{config.rangos?.general?.min}–{config.rangos?.general?.max}°C</strong> ·
        Refrigerador <strong>{config.rangos?.refrigerador?.min}–{config.rangos?.refrigerador?.max}°C</strong> ·
        Congelador <strong>{config.rangos?.congelador?.min}–{config.rangos?.congelador?.max}°C</strong>. Valores fuera de rango se resaltan en rojo y crean una incidencia automática.
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table text-xs">
          <thead>
            <tr>
              <th rowSpan={2}>Franja</th>
              <th colSpan={2} className="bg-[#e9f2ee] text-center">General</th>
              <th colSpan={2} className="bg-[#e9f2ee] text-center">Refrigerador</th>
              <th colSpan={2} className="bg-[#e9f2ee] text-center">Congelador</th>
              <th rowSpan={2}>Observación</th>
              <th rowSpan={2} className="w-16"></th>
            </tr>
            <tr>
              <th className="text-center">°C</th><th className="text-center">%H</th>
              <th className="text-center">°C</th><th className="text-center">%H</th>
              <th className="text-center">°C</th><th className="text-center">%H</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => {
              const fueraGen = fueraRango(f.temp_general, "general");
              const fueraRef = fueraRango(f.temp_refrigerador, "refrigerador");
              const fueraCong = fueraRango(f.temp_congelador, "congelador");
              return (
                <tr key={f.hora_inicio}>
                  <td className="font-mono whitespace-nowrap">
                    {f.hora_inicio}-{f.hora_fin}
                    {f.guardado && <Badge variant="outline" className="ml-2 text-[9px]">✓</Badge>}
                  </td>
                  <td><Input className={`w-16 ${fueraGen ? "border-[#b53a25] text-[#b53a25] font-semibold" : ""}`} type="number" step="0.1" value={f.temp_general} onChange={e => updateFila(i, "temp_general", e.target.value)} data-testid={`temp-gen-${i}`} /></td>
                  <td><Input className="w-14" type="number" step="0.1" value={f.hum_general} onChange={e => updateFila(i, "hum_general", e.target.value)} /></td>
                  <td><Input className={`w-16 ${fueraRef ? "border-[#b53a25] text-[#b53a25] font-semibold" : ""}`} type="number" step="0.1" value={f.temp_refrigerador} onChange={e => updateFila(i, "temp_refrigerador", e.target.value)} data-testid={`temp-ref-${i}`} /></td>
                  <td><Input className="w-14" type="number" step="0.1" value={f.hum_refrigerador} onChange={e => updateFila(i, "hum_refrigerador", e.target.value)} /></td>
                  <td><Input className={`w-16 ${fueraCong ? "border-[#b53a25] text-[#b53a25] font-semibold" : ""}`} type="number" step="0.1" value={f.temp_congelador} onChange={e => updateFila(i, "temp_congelador", e.target.value)} data-testid={`temp-cong-${i}`} /></td>
                  <td><Input className="w-14" type="number" step="0.1" value={f.hum_congelador} onChange={e => updateFila(i, "hum_congelador", e.target.value)} /></td>
                  <td><Input value={f.observacion} onChange={e => updateFila(i, "observacion", e.target.value)} className="min-w-[150px]" /></td>
                  <td><Button size="sm" variant="outline" onClick={() => guardarFila(i)} data-testid={`temp-save-${i}`} title="Guardar fila"><Save size={12} /></Button></td>
                </tr>
              );
            })}
            {filas.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-[#8ba397]">Cargando franjas...</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#d1dbd6] rounded-md p-4">
        <h3 className="font-semibold text-sm text-[#15241d] mb-3"><FileDown size={14} className="inline mr-1" />Generar PDF de registros</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Desde</Label><Input type="date" value={pdfDesde} onChange={e => setPdfDesde(e.target.value)} data-testid="temp-pdf-desde" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={pdfHasta} onChange={e => setPdfHasta(e.target.value)} data-testid="temp-pdf-hasta" /></div>
          <Button onClick={() => downloadPDF(`/warehouse-ops/temperatura-franjas/pdf?desde=${pdfDesde}&hasta=${pdfHasta}&bodega_id=${bodegaId}`)} className="bg-[#205c45]" data-testid="temp-pdf-btn">
            <FileDown size={14} className="mr-1" />Descargar PDF
          </Button>
        </div>
      </div>

      {fueraRangoCount > 0 && (
        <div className="bg-[#fdecea] border border-[#d99584] rounded-md p-3 text-sm text-[#7a2514]">
          <AlertTriangle size={14} className="inline mr-1" />
          Hay <strong>{fueraRangoCount}</strong> franjas con temperatura fuera de rango en los últimos 30 días. Incidencias regulatorias creadas automáticamente.
        </div>
      )}
    </div>
  );
}

function LimpiezaTurnos() {
  const bodegas = useBodegas();
  const [bodegaId, setBodegaId] = useState("");
  const [config, setConfig] = useState({ turnos: [] });
  const [fechaForm, setFechaForm] = useState(todayISO());
  const [filas, setFilas] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [pdfDesde, setPdfDesde] = useState(monthAgoISO());
  const [pdfHasta, setPdfHasta] = useState(todayISO());

  useEffect(() => {
    api.get("/warehouse-ops/limpieza-turnos/config").then(r => setConfig(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (bodegas.length && !bodegaId) setBodegaId(bodegas[0].id);
  }, [bodegas, bodegaId]);

  const loadDia = useCallback(async () => {
    if (!bodegaId || !fechaForm || !config.turnos?.length) return;
    try {
      const { data } = await api.get(`/warehouse-ops/limpieza-turnos?bodega_id=${bodegaId}&desde=${fechaForm}&hasta=${fechaForm}`);
      const map = Object.fromEntries(data.map(r => [r.turno, r]));
      setFilas(config.turnos.map(t => {
        const e = map[t.value] || {};
        return {
          turno: t.value, label: t.label, hora_inicio: t.hora_inicio, hora_fin: t.hora_fin,
          area: e.area ?? "", actividad: e.actividad ?? "",
          encargado_nombre: e.encargado_nombre ?? "", observacion: e.observacion ?? "",
          guardado: !!e.id,
        };
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [bodegaId, fechaForm, config.turnos]);

  const loadHistorico = useCallback(async () => {
    if (!bodegaId) return;
    try {
      const { data } = await api.get(`/warehouse-ops/limpieza-turnos?bodega_id=${bodegaId}&desde=${monthAgoISO()}&hasta=${todayISO()}`);
      setHistorico(data);
    } catch (err) { console.warn("loadHistorico limpieza:", err); }
  }, [bodegaId]);

  useEffect(() => { loadDia(); loadHistorico(); }, [loadDia, loadHistorico]);

  const updateFila = (idx, field, value) => {
    setFilas(f => { const c = [...f]; c[idx] = { ...c[idx], [field]: value }; return c; });
  };

  const guardarFila = async (idx) => {
    const f = filas[idx];
    if (!bodegaId) return toast.error("Seleccione una bodega");
    if (!f.area || !f.actividad || !f.encargado_nombre) return toast.error("Complete área, actividad y encargado");
    try {
      await api.post("/warehouse-ops/limpieza-turnos", {
        bodega_id: bodegaId, fecha: fechaForm, turno: f.turno,
        area: f.area, actividad: f.actividad,
        encargado_nombre: f.encargado_nombre, observacion: f.observacion,
      });
      toast.success(`Turno ${f.label} registrado`);
      loadDia(); loadHistorico();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white border border-[#d1dbd6] rounded-md p-3">
        <div className="flex-1 min-w-[180px]">
          <Label>Bodega</Label>
          <Select value={bodegaId} onValueChange={setBodegaId}>
            <SelectTrigger data-testid="limp-bodega"><SelectValue placeholder="..." /></SelectTrigger>
            <SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Fecha</Label>
          <Input type="date" value={fechaForm} onChange={(e) => setFechaForm(e.target.value)} data-testid="limp-fecha" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filas.map((f, i) => (
          <div key={f.turno} className="bg-white border border-[#d1dbd6] rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-base text-[#15241d]">{f.label} <span className="text-xs text-[#5c7368] font-normal">{f.hora_inicio}-{f.hora_fin}</span></h3>
              {f.guardado && <Badge className="bg-[#def5e6] text-[#1f6b42] border-[#9dd4ae] border">Registrado</Badge>}
            </div>
            <div><Label>Área</Label><Input value={f.area} onChange={e => updateFila(i, "area", e.target.value)} placeholder="Ej: Estanterías refrigerador" data-testid={`limp-area-${f.turno}`} /></div>
            <div><Label>Actividad realizada</Label><Input value={f.actividad} onChange={e => updateFila(i, "actividad", e.target.value)} placeholder="Ej: Sanitización con alcohol 70%" data-testid={`limp-act-${f.turno}`} /></div>
            <div><Label>Encargado</Label><Input value={f.encargado_nombre} onChange={e => updateFila(i, "encargado_nombre", e.target.value)} placeholder="Nombre del responsable" data-testid={`limp-enc-${f.turno}`} /></div>
            <div><Label>Observación</Label><Input value={f.observacion} onChange={e => updateFila(i, "observacion", e.target.value)} /></div>
            <Button onClick={() => guardarFila(i)} className="bg-[#205c45] w-full" data-testid={`limp-save-${f.turno}`}><Save size={14} className="mr-1" />Registrar turno</Button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#d1dbd6] rounded-md p-4">
        <h3 className="font-semibold text-sm text-[#15241d] mb-3"><FileDown size={14} className="inline mr-1" />Generar PDF de registros</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Desde</Label><Input type="date" value={pdfDesde} onChange={e => setPdfDesde(e.target.value)} data-testid="limp-pdf-desde" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={pdfHasta} onChange={e => setPdfHasta(e.target.value)} data-testid="limp-pdf-hasta" /></div>
          <Button onClick={() => downloadPDF(`/warehouse-ops/limpieza-turnos/pdf?desde=${pdfDesde}&hasta=${pdfHasta}&bodega_id=${bodegaId}`)} className="bg-[#205c45]" data-testid="limp-pdf-btn">
            <FileDown size={14} className="mr-1" />Descargar PDF
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-[#e9f2ee] text-xs uppercase tracking-wider text-[#205c45] font-semibold border-b border-[#d1dbd6]">Histórico (últimos 30 días)</div>
        <table className="erp-table text-xs">
          <thead><tr><th>Fecha</th><th>Turno</th><th>Área</th><th>Actividad</th><th>Encargado</th></tr></thead>
          <tbody>
            {historico.slice(0, 30).map(r => (
              <tr key={r.id}>
                <td>{r.fecha}</td>
                <td><Badge variant="outline" className="text-[10px]">{r.turno === "manana" ? "Mañana" : "Tarde"}</Badge></td>
                <td>{r.area}</td>
                <td>{r.actividad}</td>
                <td>{r.encargado_nombre}</td>
              </tr>
            ))}
            {historico.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#8ba397]">Sin registros</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Ubicaciones() {
  const bodegas = useBodegas();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ bodega_id: "", codigo: "", descripcion: "", tipo: "estanteria" });
  const load = useCallback(() => api.get("/warehouse-ops/ubicaciones").then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.bodega_id || !form.codigo) return toast.error("Bodega y código requeridos");
    try { await api.post("/warehouse-ops/ubicaciones", form); toast.success("Ubicación creada"); setForm({ ...form, codigo: "", descripcion: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-[#d1dbd6] bg-white p-4 grid md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2"><Label>Bodega</Label><Select value={form.bodega_id} onValueChange={(v) => setForm({ ...form, bodega_id: v })}><SelectTrigger><SelectValue placeholder="..." /></SelectTrigger><SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Código</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="E1-A-03" /></div>
        <div><Label>Tipo</Label><Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="estanteria">Estantería</SelectItem><SelectItem value="refrigerador">Refrigerador</SelectItem><SelectItem value="congelador">Congelador</SelectItem><SelectItem value="cuarentena">Cuarentena</SelectItem></SelectContent></Select></div>
        <div><Button onClick={save} className="bg-[#205c45] w-full" data-testid="save-ubi-btn"><Plus size={14} className="mr-1" />Crear</Button></div>
        <div className="md:col-span-5"><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Estantería 1 - Fila A - Posición 3" /></div>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Código</th><th>Bodega</th><th>Tipo</th><th>Descripción</th><th>Estado</th></tr></thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id}>
                <td className="font-mono text-xs font-semibold">{u.codigo}</td>
                <td>{bodegas.find(b => b.id === u.bodega_id)?.nombre || u.bodega_id?.slice(0, 8)}</td>
                <td className="capitalize">{u.tipo}</td>
                <td>{u.descripcion || "—"}</td>
                <td>{u.activo ? "Activa" : "Inactiva"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#8ba397]">Sin ubicaciones</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
